"use client";
import { AnimatePresence, motion } from "framer-motion";
import { Coins, Flame, Mail, MapPin, Star, Ticket, Users } from "lucide-react";
import { MessageCircle } from "lucide-react";
import { useState } from "react";

const TeamBuildingPage = () => {
	const [showCopyNotification, setShowCopyNotification] = useState(false);

	return (
		<main className="bg-black text-white">
			{/* Hero Section */}
			<section
				className="relative py-32 bg-cover bg-center"
				style={{ backgroundImage: "url('/images/eventos_header.webp')" }}
			>
				<div className="absolute inset-0 bg-black/70"></div>
				<div className="container mx-auto px-4 max-w-4xl relative z-10">
					<motion.h1
						className="font-amarante text-5xl md:text-7xl mb-6 text-center text-white"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
					>
						<span className="headerFont inline-block pb-2 border-b-2 border-red-700">Team Building</span>
					</motion.h1>

					<motion.p
						className="text-2xl text-gray-300 max-w-3xl mx-auto text-center mb-8"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 0.6 }}
					>
						<strong className="text-red-400">
							Une a tu equipo con una aventura épica
							<br />
							<span className="text-xl italic text-gray-300">
								Una experiencia única que fortalece los vínculos, fomenta la colaboración y crea recuerdos
								inolvidables
							</span>
						</strong>
					</motion.p>

					<motion.div
						className="text-center"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.6, duration: 0.6 }}
					>
						<p className="text-xl text-gray-200 max-w-2xl mx-auto mb-6">
							Transforma tu próximo evento corporativo en una aventura memorable con{" "}
							<span className="font-amarante text-red-400">Los Amos del Calabozo</span>.
						</p>
						<p className="text-lg text-white bg-red-800/50 rounded-lg py-2 px-4 inline-block">
							Todas nuestras partidas están disponibles tanto en castellano como{" "}
							<a href="/aprende-ingles" className="text-red-400 underline">
								en inglés.
							</a>
						</p>
					</motion.div>
				</div>
			</section>

			{/* Main Content Section */}
			<section className="py-16 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.h2
						className="text-3xl font-amarante headerFont mb-12 border-b-2 border-red-700 pb-2 inline-block"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<Flame className="inline mr-3 text-red-500" size={28} />
						Team Building con Rol
					</motion.h2>

					<motion.div
						className="bg-gray-800 rounded-lg overflow-hidden border-l-4 border-red-500"
						initial={{ opacity: 0, x: -20 }}
						whileInView={{ opacity: 1, x: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6 }}
					>
						<div className="p-8">
							<div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-6">
								<div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
									<Users className="text-white" size={36} />
								</div>
								<div>
									<h3 className="text-3xl font-amarante headerFont text-red-400">
										La Aventura Corporativa Definitiva
									</h3>
								</div>
							</div>

							<div className="space-y-6">
								<p className="text-gray-300">
									Si lo que quieres es unir a los miembros de tu equipo, abandonarlos en una isla desierta en
									medio del pacífico es una de las mejores maneras de hacerlo.
								</p>

								<p className="text-gray-300">
									Si además no quieres cometer varios crímenes en el proceso, ¿por qué no embarcarlos en una
									partida de <strong>Cocos & Corazones</strong>? Una partida de 2 a 4 horas desde 15€ por
									jugador donde tendrán que sobrevivir peligros desconocidos. Y si las aventuras tropicales no
									son lo tuyo, prueba alguna de las otras ambientaciones que ofrecemos.
								</p>

								<p className="text-gray-300">
									Usando un sencillo sistema adaptado de <strong>Láseres & Sentimientos</strong>, tienes todas
									las facilidades para que la próxima reunión a la que vayas sea la más divertida de tu vida.
								</p>

								<div className="mt-6">
									<h4 className="text-xl font-amarante text-red-300 mb-4">
										<Star className="inline mr-2" size={20} />
										Características del evento:
									</h4>
									<ul className="list-none grid grid-cols-1 md:grid-cols-2 gap-3">
										{[
											"Fomenta el trabajo en equipo",
											"Desafía la resolución creativa de problemas",
											"Crea vínculos duraderos entre los miembros del equipo",
											"Adaptable a cualquier grupo y tamaño de equipo",
											"Disponible en castellano e inglés",
										].map((feature, idx) => (
											<li key={idx} className="text-gray-300 flex items-start">
												<span className="text-red-400 mr-2">•</span> {feature}
											</li>
										))}
									</ul>
								</div>

								<div className="bg-red-600/20 p-6 rounded-lg mt-6 border border-red-800">
									<div className="flex items-center gap-3 mb-3">
										<MapPin className="text-red-400" size={20} />
										<h4 className="text-lg font-amarante text-red-400">¿Dónde y cómo?</h4>
									</div>
									<p className="text-gray-300">
										Nos desplazamos a tu ubicación dentro de la Comunidad de Madrid o podemos facilitar el
										espacio para tu evento. Contacta con nosotros para discutir los detalles y crear una
										experiencia totalmente personalizada.
									</p>
								</div>

								<div className="bg-red-600/20 p-6 rounded-lg mt-2 border border-red-800">
									<div className="flex items-center gap-3 mb-3">
										<Coins className="text-red-400" size={20} />
										<h4 className="text-lg font-amarante text-red-400">Tarifas</h4>
									</div>
									<p className="text-gray-300">
										Precios desde 15€ por participante, dependiendo del tipo de evento, duración y necesidades
										específicas. Solicita un presupuesto personalizado sin compromiso.
									</p>
								</div>
							</div>
						</div>
					</motion.div>
				</div>
			</section>

			{/* Final CTA */}
			<section className="py-16 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="bg-gray-800 rounded-lg p-8 text-center border-4 border-red-700"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<motion.div
							whileHover={{ scale: 1.05 }}
							className="mx-auto w-24 h-24 bg-red-600 rounded-full flex items-center justify-center mb-6"
						>
							<Ticket className="text-white" size={48} />
						</motion.div>
						<h2 className="text-3xl font-amarante headerFont text-red-400 mb-4">
							¿Listo para la aventura corporativa?
						</h2>
						<p className="text-gray-300 text-xl mb-8">
							Transforma tu próximo evento de team building en una experiencia extraordinaria que tu equipo
							recordará durante años.
						</p>
						<motion.div
							className="flex flex-col md:flex-row items-center justify-center gap-4"
							whileInView={{ opacity: 1 }}
							viewport={{ once: true }}
						>
							<motion.a
								href="mailto:somos@losamosdelcalabozo.com?subject=Consulta sobre Team Building"
								className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
								whileHover={{
									scale: 1.1,
									boxShadow: "0 0 15px #f93d3d",
									backgroundColor: "white",
									color: "black",
									fontWeight: "900",
									fontStyle: "italic",
								}}
								whileTap={{ scale: 0.98 }}
							>
								<Mail className="inline mr-2" size={20} />
								Reservar Team Building
							</motion.a>

							<motion.a
								href={`https://api.whatsapp.com/send?phone=34642367972&text=${encodeURIComponent("Hola, me gustaría obtener más información sobre vuestro team building. ¿Podéis ayudarme?")}`}
								className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
								whileHover={{
									scale: 1.05,
									boxShadow: "0 0 15px #22c55e",
								}}
								whileTap={{ scale: 0.98 }}
								target="_blank"
								rel="noopener noreferrer"
							>
								<MessageCircle className="inline mr-2" size={20} />
								Consultar por WhatsApp
							</motion.a>
						</motion.div>
					</motion.div>
				</div>
			</section>

			{/* Main Contact Methods */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="mt-8 sm:mt-10 md:mt-12 bg-gray-800 rounded-lg p-4 sm:p-6 md:p-8 text-center relative"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.8, duration: 0.6 }}
					>
						<h3 className="text-xl sm:text-2xl font-amarante headerFont text-red-400 mb-4">
							Nuestro Pergamino Oficial
						</h3>
						<motion.div
							className="text-lg sm:text-2xl md:text-4xl font-bold text-white bg-gray-700 py-3 px-2 sm:px-4 rounded-lg select-all cursor-pointer"
							whileHover={{
								backgroundColor: "#ef4444",
								color: "white",
								transition: { duration: 0.3 },
							}}
							onClick={() => {
								navigator.clipboard.writeText("somos@losamosdelcalabozo.com");
								setShowCopyNotification(true);
								setTimeout(() => setShowCopyNotification(false), 2000);
							}}
						>
							somos@losamosdelcalabozo.com
						</motion.div>
						<p className="text-gray-400 mt-2 text-xs sm:text-sm italic">
							Haz click para copiar a tu grimorio de hechizos (portapapeles)
						</p>
						<AnimatePresence>
							{showCopyNotification && (
								<motion.div
									className="bg-green-600 text-white text-sm py-2 px-4 rounded-md absolute left-1/2 transform -translate-x-1/2 mt-2"
									initial={{ opacity: 0, y: -10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0 }}
								>
									Email copiado al portapapeles
								</motion.div>
							)}
						</AnimatePresence>
					</motion.div>
				</div>
			</section>

			{/* Easter Egg */}
			<section className="py-4 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="text-center opacity-30 hover:opacity-100 transition-opacity cursor-pointer"
						whileHover={{ scale: 1.01 }}
						whileTap={{ scale: 0.99 }}
					>
						<p className="text-sm text-gray-500 italic">
							Los equipos que se aventuran juntos, permanecen juntos...
						</p>
					</motion.div>
				</div>
			</section>
		</main>
	);
};

export default TeamBuildingPage;
